import React from "react";
import { LegalLayout, Section, Bullets } from "@/components/legal/LegalLayout";
import { PRIVACY_EMAIL } from "@/constants/legal";

export default function Privacy() {
  return (
    <LegalLayout
      slug="privacy"
      title="Privacy Policy"
      subtitle="HostGuard Australia (“HostGuard”, “we”, “us”, “our”) is committed to protecting your privacy and handling personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs)."
    >
      <Section title="1. Our privacy-first approach">
        <p>HostGuard is built to be privacy-first and compliance-focused. We do <strong>not</strong> operate a public guest database, we do <strong>not</strong> store guests’ full names, and we do <strong>not</strong> allow searching for individuals by name. Guests are referenced only by the initials you choose to enter. HostGuard is a tool for assessing booking characteristics — not for identifying or profiling individuals.</p>
      </Section>

      <Section title="2. Information we collect">
        <p>We only collect information reasonably necessary to provide our services:</p>
        <p className="font-medium text-foreground">Account information</p>
        <Bullets items={[
          "Your email address, password (stored only as a secure hash), and optional business name and full name.",
          "Account preferences and settings.",
        ]} />
        <p className="font-medium text-foreground">Billing information</p>
        <Bullets items={[
          "Subscription plan, billing status, and renewal dates.",
          "Payment card details are collected and processed directly by Stripe — HostGuard never receives or stores your full card number.",
        ]} />
        <p className="font-medium text-foreground">Assessment data</p>
        <Bullets items={[
          "The booking characteristics you enter for an assessment (for example: platform, lead time, number of reviews, group size, message quality and guest initials).",
          "Computed risk scores, sub-scores and AI-generated summaries associated with your account.",
        ]} />
        <p className="font-medium text-foreground">Cookies and analytics</p>
        <Bullets items={[
          "Essential and authentication cookies required to operate the service.",
          "Limited, aggregated analytics and performance data to help us improve reliability and usability. See our Cookie Policy for detail.",
        ]} />
      </Section>

      <Section title="3. How we use your information">
        <p>We use your information to calculate risk scores, generate cautious AI summaries (on paid plans), maintain your private assessment history, process subscription billing, provide support, secure the service, and meet our legal obligations. We do not use your data to make automated decisions that have legal or similarly significant effects on any individual.</p>
      </Section>

      <Section title="4. How your data is stored">
        <p>Your data is stored in secured, access-controlled databases. Assessments, properties and subscription records are logically isolated per user account so that one customer can never access another customer’s data. We retain data only for as long as needed to provide the service or as required by law.</p>
      </Section>

      <Section title="5. How your data is secured">
        <Bullets items={[
          "Passwords are hashed using industry-standard one-way hashing (bcrypt) and are never stored in plain text.",
          "Access to the application requires authentication, and data is transmitted over encrypted connections (HTTPS/TLS).",
          "We apply reasonable technical and organisational measures to protect against loss, misuse and unauthorised access.",
        ]} />
        <p>No method of transmission or storage is 100% secure, but we take security seriously and review our controls regularly.</p>
      </Section>

      <Section title="6. Your rights">
        <p>Under the Australian Privacy Principles you have the right to:</p>
        <Bullets items={[
          "Request access to the personal information we hold about you.",
          "Request correction of information that is inaccurate, out of date or incomplete.",
          "Request deletion of your account data, subject to legal retention requirements.",
          "Withdraw consent or object to certain processing where applicable.",
          "Make a complaint if you believe we have breached the APPs.",
        ]} />
        <p>You can delete individual assessments and properties at any time from within the app, or contact us to action a broader request.</p>
      </Section>

      <Section title="7. Data retention">
        <p>We retain account and assessment data while your account is active. If you close your account, we will delete or de-identify your personal information within a reasonable period, unless we are required to retain it to comply with legal, tax or accounting obligations. Aggregated, de-identified data may be retained for analytics.</p>
      </Section>

      <Section title="8. Third-party providers">
        <p>We share limited information with trusted service providers strictly to operate HostGuard:</p>
        <p className="font-medium text-foreground">Stripe (payments)</p>
        <p>Subscription payments are processed by Stripe. Stripe handles your card details directly under PCI-DSS standards. We receive only billing status and metadata, never your full card number.</p>
        <p className="font-medium text-foreground">AI providers</p>
        <p>On paid plans, the booking characteristics and computed risk factors (never a guest’s full name) are sent to a third-party AI provider to generate a neutral, non-accusatory summary. This data is used only to produce your summary.</p>
        <p>We require our providers to handle data securely and only for the purposes we specify.</p>
      </Section>

      <Section title="9. We do not sell your personal data">
        <p>HostGuard does <strong>not</strong> sell, rent or trade your personal information or your guests’ information to any third party for marketing or any other purpose.</p>
      </Section>

      <Section title="10. Anti-discrimination">
        <p>HostGuard must not be used to unlawfully discriminate. Risk scores are based on booking characteristics, not protected personal attributes, and you remain responsible for complying with anti-discrimination laws.</p>
      </Section>

      <Section title="11. Changes and contact">
        <p>We may update this Privacy Policy from time to time. Material changes will be reflected by the “Last updated” date above. For privacy questions, access or correction requests, or complaints, contact us at <a href={`mailto:${PRIVACY_EMAIL}`} className="font-medium text-primary hover:underline">{PRIVACY_EMAIL}</a> or via our <a href="/contact" className="font-medium text-primary hover:underline">Contact page</a>.</p>
      </Section>
    </LegalLayout>
  );
}
